import React, { useCallback, useMemo } from "react";
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import {
  useNotificationsControllerGetNotifications,
  getNotificationsControllerGetNotificationsQueryKey,
  notificationsControllerGetNotifications,
  useNotificationsControllerGetUnreadCount,
  useNotificationsControllerMarkAllAsRead,
  useNotificationsControllerMarkAsRead,
  getNotificationsControllerGetUnreadCountQueryKey,
  type NotificationResponseDto,
  type NotificationsControllerGetUnreadCount200,
  type NotificationsControllerGetNotifications200,
} from "@semo/api-contracts";
import { convertNotificationToDisplay } from "../utils/notification-utils";
import type { DisplayNotification } from "../utils/notification-types";
import { useSearchParams } from "react-router-dom";

export type NotificationDisplayType = "dropdown" | "page";

export interface NotificationContainerProps {
  type: NotificationDisplayType;
  children: (props: NotificationContainerChildProps) => React.ReactNode;
  onTabChange?: (tab: string) => void;
}

export interface NotificationContainerChildProps {
  notifications: DisplayNotification[];
  total: number;
  unreadCount: number;
  isLoading: boolean;
  error: Error | null;
  isFetchingNextPage: boolean;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  markAllAsRead: () => void;
  onRead: (id: string) => Promise<void>;
  onItemClick: (
    resourceType?: string,
    resourceId?: string,
    id?: string,
    isRead?: boolean,
  ) => void;
  isMarkingAsRead: boolean;
  hasUnread: boolean;
  type: NotificationDisplayType;
}

export const VendorNotificationContainer: React.FC<NotificationContainerProps> = ({
  type,
  children,
  onTabChange,
}) => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  const limit = type === "dropdown" ? 10 : 20;
  const listQueryKey = [
    ...getNotificationsControllerGetNotificationsQueryKey({ limit, page: 1 }),
    "infinite",
  ];
  const unreadQueryKey = getNotificationsControllerGetUnreadCountQueryKey();

  const {
    data: infiniteData,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteQuery({
    queryKey: listQueryKey,
    queryFn: async ({ pageParam = 1 }) => {
      return notificationsControllerGetNotifications({
        page: pageParam,
        limit,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const responseData = lastPage.data;
      const totalItems = responseData?.meta?.totalCount ?? 0;
      const loadedItems = allPages.reduce(
        (acc, page) => acc + (page.data?.items?.length ?? 0),
        0,
      );
      return loadedItems < totalItems ? allPages.length + 1 : undefined;
    },
    initialPageParam: 1,
  });

  const notifications = useMemo(() => {
    return infiniteData?.pages.flatMap((page) => page.data?.items ?? []) ?? [];
  }, [infiniteData]);

  const total = infiniteData?.pages[0]?.data?.meta?.totalCount ?? 0;

  const displayNotifications = useMemo(
    () =>
      notifications.map((n: NotificationResponseDto) =>
        convertNotificationToDisplay(n),
      ),
    [notifications],
  );

  const { data: unreadCountResponse } =
    useNotificationsControllerGetUnreadCount();
  const unreadCount = unreadCountResponse?.data?.count ?? 0;

  const hasUnread = useMemo(
    () => notifications.some((n: NotificationResponseDto) => !n.isRead),
    [notifications],
  );

  const { mutateAsync: markAsReadMutate } =
    useNotificationsControllerMarkAsRead({
      mutation: {
        onMutate: async ({ id }) => {
          await queryClient.cancelQueries({ queryKey: listQueryKey });
          await queryClient.cancelQueries({ queryKey: unreadQueryKey });

          const previousList =
            queryClient.getQueryData<
              InfiniteData<NotificationsControllerGetNotifications200>
            >(listQueryKey);
          const previousUnread =
            queryClient.getQueryData<NotificationsControllerGetUnreadCount200>(
              unreadQueryKey,
            );

          queryClient.setQueryData<
            InfiniteData<NotificationsControllerGetNotifications200>
          >(listQueryKey, (old) => {
            if (!old) return old;
            return {
              ...old,
              pages: old.pages.map((page) => {
                if (!page.data) return page;
                return {
                  ...page,
                  data: {
                    ...page.data,
                    items:
                      page.data.items?.map((item: NotificationResponseDto) =>
                        item.id === id ? { ...item, isRead: true } : item,
                      ) ?? [],
                  },
                };
              }),
            };
          });

          queryClient.setQueryData<NotificationsControllerGetUnreadCount200>(
            unreadQueryKey,
            (old) => {
              if (!old || !old.data) return old;
              const currentCount = old.data.count ?? 0;
              return {
                ...old,
                data: {
                  ...old.data,
                  count: Math.max(0, currentCount - 1),
                },
              };
            },
          );

          return { previousList, previousUnread };
        },
        onError: (err, newTodo, context) => {
          if (context?.previousList) {
            queryClient.setQueryData(listQueryKey, context.previousList);
          }
          if (context?.previousUnread) {
            queryClient.setQueryData(unreadQueryKey, context.previousUnread);
          }
        },
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getNotificationsControllerGetNotificationsQueryKey(),
          });
          queryClient.invalidateQueries({ queryKey: unreadQueryKey });
        },
      },
    });

  const onRead = useCallback(
    async (id: string) => {
      await markAsReadMutate({ id });
    },
    [markAsReadMutate],
  );

  const { mutate: markAllAsReadMutate, isPending: isMarkingAsRead } =
    useNotificationsControllerMarkAllAsRead({
      mutation: {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: getNotificationsControllerGetNotificationsQueryKey(),
          });
          queryClient.invalidateQueries({
            queryKey: getNotificationsControllerGetUnreadCountQueryKey(),
          });
        },
      },
    });

  const markAllAsRead = useCallback(() => {
    markAllAsReadMutate({ data: {} });
  }, [markAllAsReadMutate]);

  const onItemClick = useCallback(
    async (
      resourceType?: string,
      resourceId?: string,
      id?: string,
      isRead?: boolean,
    ) => {
      if (id && !isRead && !isLoading) {
        onRead(id).catch((err) =>
          console.error("Failed to mark as read:", err),
        );
      }

      // 벤더(Embed) 대시보드의 라우팅 로직 (탭 전환)
      if (resourceType) {
        let targetTab = "all-projects";
        switch (resourceType) {
          case "quote":
          case "project":
            targetTab = "all-projects"; // 프로젝트 목록이나 응답 탭
            break;
          case "chat":
            targetTab = "chat-rooms";
            break;
          case "contract":
            targetTab = "contracts";
            break;
          case "settlement":
            targetTab = "settlements";
            break;
          case "payment":
            targetTab = "settlements"; // 혹은 contracts
            break;
        }

        if (onTabChange) {
          onTabChange(targetTab);
        } else {
          const newParams = new URLSearchParams(searchParams);
          newParams.set("tab", targetTab);
          setSearchParams(newParams, { replace: true });
        }
      }
    },
    [onRead, isLoading, onTabChange, searchParams, setSearchParams],
  );

  const childProps: NotificationContainerChildProps = {
    notifications: displayNotifications,
    total,
    unreadCount,
    isLoading,
    error: error || null,
    isFetchingNextPage,
    hasNextPage: hasNextPage ?? false,
    fetchNextPage,
    markAllAsRead,
    onRead,
    onItemClick,
    isMarkingAsRead,
    hasUnread,
    type,
  };

  return <>{children(childProps)}</>;
};
